import React from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Sparkles,
  MapPin,
  User,
  LogIn,
  ChevronLeft,
  ChevronRight,
  Zap,
  Heart,
  BookOpen,
  Headphones,
  ShieldCheck,
  Package,
  Search,
  ClipboardCheck,
  TrendingUp,
  HeartHandshake,
  Clock,
  Lock,
  Image as ImageIcon,
  MessageSquare,
  Tag,
  GraduationCap,
} from 'lucide-react';

type PublicAgoraPageProps = {
  isAuthenticated: boolean;
  onBack: () => void;
  onExplore: () => void;
  onRegisterClick: () => void;
  onLoginClick: () => void;
};

type LessonBadge = 'preview' | 'full';

const TRAILS: {
  title: string;
  description: string;
  Icon: LucideIcon;
  iconWrap: string;
  lessons: { title: string; badge: LessonBadge }[];
}[] = [
  {
    title: 'Comece do jeito certo',
    description: 'Monte sua operação com clareza, organização e confiança desde o primeiro dia.',
    Icon: ClipboardCheck,
    iconWrap: 'from-emerald-500/30 to-teal-500/15',
    lessons: [
      { title: 'Checklist para começar a vender hoje', badge: 'preview' },
      { title: 'Defina preço e margem sem complicação', badge: 'full' },
      { title: 'Rotina semanal para não perder pedidos', badge: 'full' },
    ],
  },
  {
    title: 'Venda mais todos os dias',
    description: 'Ações simples que aumentam visibilidade, conversão e ticket médio no dia a dia.',
    Icon: TrendingUp,
    iconWrap: 'from-orange-500/30 to-amber-500/15',
    lessons: [
      { title: 'Como destacar seus melhores produtos', badge: 'preview' },
      { title: 'Mensagens prontas para fechar mais vendas', badge: 'full' },
      { title: 'Ofertas sazonais que não quebram a margem', badge: 'full' },
    ],
  },
  {
    title: 'Encante e fidelize clientes',
    description: 'Relacione-se melhor para gerar recompra, indicações e reputação forte.',
    Icon: HeartHandshake,
    iconWrap: 'from-rose-500/25 to-violet-500/15',
    lessons: [
      { title: 'Boas-vindas e pós-compra que geram connexão', badge: 'preview' },
      { title: 'Lidar com reclamações com empatia e solução', badge: 'full' },
      { title: 'Programa simples de indicações entre clientes', badge: 'full' },
    ],
  },
];

const HIGHLIGHT_CARDS: {
  title: string;
  category: string;
  minutes: string;
  Icon: LucideIcon;
  gradient: string;
  premiumLockedForGuest: boolean;
}[] = [
  {
    title: 'Como montar uma vitrine que chama atenção',
    category: 'Vendas & vitrine',
    minutes: '5 min',
    Icon: Sparkles,
    gradient: 'from-emerald-500/15 to-orange-400/15',
    premiumLockedForGuest: false,
  },
  {
    title: 'Como tirar fotos melhores dos produtos',
    category: 'Conteúdo visual',
    minutes: '6 min',
    Icon: ImageIcon,
    gradient: 'from-sky-500/15 to-emerald-500/10',
    premiumLockedForGuest: true,
  },
  {
    title: 'Como responder clientes mais rápido',
    category: 'Atendimento',
    minutes: '4 min',
    Icon: MessageSquare,
    gradient: 'from-violet-500/15 to-slate-500/10',
    premiumLockedForGuest: true,
  },
  {
    title: 'Como criar promoções simples',
    category: 'Ofertas',
    minutes: '5 min',
    Icon: Tag,
    gradient: 'from-amber-500/20 to-orange-500/10',
    premiumLockedForGuest: true,
  },
];

export function PublicAgoraPage({
  isAuthenticated,
  onBack,
  onExplore,
  onRegisterClick,
  onLoginClick,
}: PublicAgoraPageProps) {
  return (
    <div className="min-h-full bg-gradient-to-b from-slate-100/80 via-white to-emerald-50/30 pb-28 sm:pb-32">
      <div className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/95 backdrop-blur-md shadow-sm shadow-slate-900/5">
        <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 py-3.5 sm:px-6">
          <button
            type="button"
            onClick={() => onBack()}
            className="touch-target flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-600 shadow-sm transition-colors hover:bg-slate-50 active:scale-[0.97]"
            aria-label="Voltar"
          >
            <ChevronLeft className="h-6 w-6" strokeWidth={2} />
          </button>
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="text-[10px] font-black uppercase tracking-widest text-emerald-600">Ágora</span>
            <span className="truncate text-sm font-semibold text-slate-700">Vitória para o seu negócio local</span>
          </div>
          <GraduationCap className="hidden h-8 w-8 shrink-0 text-emerald-500/35 sm:block" aria-hidden />
        </div>
      </div>

      {/* Hero */}
      <div className="mx-auto max-w-2xl px-4 pt-8 pb-2 sm:px-6">
        <div className="relative overflow-hidden rounded-3xl border border-white/40 bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-950 p-8 shadow-2xl shadow-emerald-900/20 sm:p-10">
          <div className="pointer-events-none absolute -left-24 top-0 h-56 w-56 rounded-full bg-emerald-500/25 blur-3xl" />
          <div className="pointer-events-none absolute -right-20 bottom-0 h-52 w-52 rounded-full bg-orange-400/20 blur-3xl" />
          <div className="relative z-10">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3.5 py-1 text-[11px] font-bold uppercase tracking-wide text-emerald-100">
              <Sparkles className="h-3.5 w-3.5 shrink-0 text-orange-300" />
              Biblioteca para Fortaleza
            </span>
            <h1 className="mt-5 font-black leading-[1.1] tracking-tight text-white sm:text-4xl text-3xl">
              Ágora do E-Ambulante
            </h1>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-white/80">
              Conteúdos, orientações e oportunidades para fortalecer quem vende, compra e movimenta Fortaleza — com
              trilhas práticas e materiais pensados para a rua e para o dia a dia do app.
            </p>
            <p className="mt-4 flex items-start gap-2.5 text-sm leading-relaxed text-white/55">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" strokeWidth={2} />
              <span>
                Explore prévias grátis abaixo. Conteúdo completo em trilhas e destaques fica disponível para quem faz
                login — integração futura à sua conta, sem mudar sua autenticação hoje.
              </span>
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-2xl space-y-14 px-4 sm:space-y-16 sm:px-6">
        {/* O que você encontra */}
        <section className="scroll-mt-4" aria-labelledby="agora-encontra">
          <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <h2 id="agora-encontra" className="text-2xl font-black tracking-tight text-slate-900 sm:text-[1.65rem]">
              O que você encontra na Ágora
            </h2>
            <p className="text-sm font-medium text-slate-500">Trilhas, destaques e orientação contínua</p>
          </div>
          <div className="grid gap-4 sm:gap-5">
            {[
              {
                title: 'Cursos rápidos e práticos',
                text: 'Aprenda sobre atendimento, vendas, organização, higiene, precificação e presença digital.',
                Icon: BookOpen,
                ring: 'from-emerald-500/25 to-teal-500/15',
              },
              {
                title: 'Orientações para vender melhor',
                text: 'Conteúdos pensados para a rotina real de ambulantes, pequenos vendedores e empreendedores locais.',
                Icon: Zap,
                ring: 'from-orange-500/25 to-amber-500/15',
              },
              {
                title: 'Conexão com a comunidade',
                text: 'Um espaço para fortalecer a economia local e aproximar vendedores, clientes e parceiros.',
                Icon: Heart,
                ring: 'from-slate-500/20 to-slate-700/12',
              },
            ].map(({ title, text, Icon, ring }) => (
              <div
                key={title}
                className="group rounded-3xl border border-slate-100/90 bg-white p-6 shadow-lg shadow-slate-900/[0.04] transition-[box-shadow,transform] hover:-translate-y-0.5 hover:shadow-xl hover:shadow-slate-900/8"
              >
                <div className={`inline-flex rounded-2xl bg-gradient-to-br p-3.5 shadow-inner shadow-white/20 ${ring}`}>
                  <Icon className="h-7 w-7 text-slate-800" strokeWidth={2} />
                </div>
                <h3 className="mt-4 text-lg font-bold text-slate-900">{title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-slate-600">{text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Trilhas */}
        <section aria-labelledby="agora-trilhas">
          <div className="mb-8 flex flex-col gap-3">
            <h2 id="agora-trilhas" className="text-2xl font-black tracking-tight text-slate-900 sm:text-[1.65rem]">
              Trilhas para evoluir seu negócio
            </h2>
            <p className="max-w-xl text-[15px] leading-relaxed text-slate-600">
              Percursos temáticos para avançar com segurança. Uma prévia gratuita por trilha; o restante como referência da
              experiência para cadastrados.
            </p>
          </div>
          <div className="space-y-6">
            {TRAILS.map((trail) => (
              <article
                key={trail.title}
                className="overflow-hidden rounded-3xl border border-slate-200/90 bg-white shadow-xl shadow-slate-900/[0.06]"
              >
                <div className="border-b border-slate-100 bg-gradient-to-br from-slate-50/90 to-white p-6 sm:flex sm:items-start sm:gap-6 sm:p-7">
                  <div
                    className={`mb-5 inline-flex rounded-2xl bg-gradient-to-br p-4 shadow-sm sm:mb-0 sm:shrink-0 ${trail.iconWrap}`}
                  >
                    <trail.Icon className="h-8 w-8 text-slate-800" strokeWidth={2} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-xl font-black text-slate-900">{trail.title}</h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-slate-600">{trail.description}</p>
                  </div>
                </div>
                <ul className="divide-y divide-slate-100">
                  {trail.lessons.map((lesson) => (
                    <li key={lesson.title} className="flex flex-col gap-2 px-6 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-7">
                      <span className="text-[15px] font-medium leading-snug text-slate-800">{lesson.title}</span>
                      {lesson.badge === 'preview' ? (
                        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-emerald-800 ring-1 ring-emerald-200/80">
                          <Sparkles className="h-3 w-3" />
                          Prévia gratuita
                        </span>
                      ) : (
                        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-700 ring-1 ring-slate-200/90">
                          <Lock className="h-3 w-3 opacity-70" />
                          Completo para cadastrados
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        {/* Conteúdos em destaque */}
        <section aria-labelledby="agora-destaques">
          <div className="mb-8">
            <h2 id="agora-destaques" className="text-2xl font-black tracking-tight text-slate-900 sm:text-[1.65rem]">
              Conteúdos em destaque
            </h2>
            <p className="mt-2 text-[15px] text-slate-600">Temas rápidos para aplicar já no próximo pedido ou na sua vitrine digital.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {HIGHLIGHT_CARDS.map((item) => {
              const lockedVisual = !isAuthenticated && item.premiumLockedForGuest;
              return (
                <article
                  key={item.title}
                  className="group relative flex flex-col rounded-3xl border border-slate-200/90 bg-white p-5 shadow-lg shadow-slate-900/[0.05] transition-all duration-300 hover:-translate-y-1 hover:border-emerald-200/80 hover:shadow-xl hover:shadow-emerald-900/10"
                >
                  {lockedVisual && (
                    <div
                      className="pointer-events-none absolute inset-0 z-[1] flex flex-col items-center justify-end rounded-3xl bg-gradient-to-t from-slate-900/85 via-slate-900/25 to-transparent px-4 pb-5 pt-16"
                      aria-hidden
                    >
                      <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-black/35 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white/95 backdrop-blur-sm">
                        <Lock className="h-3.5 w-3.5" />
                        Biblioteca cadastrados
                      </span>
                    </div>
                  )}
                  <div
                    className={`mb-4 inline-flex rounded-2xl bg-gradient-to-br p-3.5 ${item.gradient}`}
                  >
                    <item.Icon className={`h-6 w-6 ${lockedVisual ? 'text-slate-700/90' : 'text-slate-800'}`} strokeWidth={2} />
                  </div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-700/85">{item.category}</p>
                  <h3 className="mt-1 min-h-[2.75rem] text-lg font-black leading-snug text-slate-900 sm:min-h-0">{item.title}</h3>
                  <div className="mt-4 flex items-center gap-2 text-xs font-semibold text-slate-500">
                    <Clock className="h-3.5 w-3.5 shrink-0" />
                    <span>{item.minutes}</span>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        {/* Prévia / Premium info */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-3xl border border-emerald-200/70 bg-gradient-to-br from-emerald-600 via-emerald-600 to-teal-700 p-7 text-white shadow-xl shadow-emerald-700/25">
            <Headphones className="mb-3 h-9 w-9 text-emerald-100 opacity-95" strokeWidth={1.75} />
            <p className="text-xs font-black uppercase tracking-widest text-emerald-50/95">Prévia liberada</p>
            <p className="mt-3 text-[15px] leading-relaxed text-emerald-50">
              Materiais públicos nesta página mostram como a Ágora orienta você na prática, sem obrigar cadastro só para entender o valor do espaço.
            </p>
          </div>
          <div className="relative overflow-hidden rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950 p-7 text-white shadow-xl">
            <div className="pointer-events-none absolute right-4 top-4 h-20 w-20 rounded-full bg-emerald-500/10 blur-2xl" />
            <ShieldCheck className="relative z-[1] mb-3 h-9 w-9 text-emerald-400" strokeWidth={1.75} />
            <p className="relative z-[1] text-xs font-black uppercase tracking-widest text-slate-400">
              Conteúdo completo para cadastrados
            </p>
            <p className="relative z-[1] mt-3 text-[15px] leading-relaxed text-slate-200">
              Trilhas fechadas, biblioteca ampla e vantagens contínuas serão disponibilizadas na conta logada em versões seguintes da Ágora.
            </p>
            <div className="relative z-[1] mt-6 inline-flex items-center gap-2 rounded-full border border-dashed border-white/20 bg-white/5 px-4 py-2 text-[11px] font-semibold text-white/90">
              <Package className="h-4 w-4" />
              Apenas visualização — sem liberação real nesta página
            </div>
          </div>
        </div>

        {/* CTAs */}
        <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-slate-50/95 p-6 shadow-2xl shadow-slate-900/10 sm:p-8">
          {isAuthenticated ? (
            <>
              <p className="mb-6 text-center text-[15px] leading-relaxed text-slate-600">
                Em breve, a Ágora conectará automaticamente aos seus conteúdos completos dentro da conta. Por enquanto,
                você pode explorar ambulantes e seguir usando o marketplace normalmente.
              </p>
              <button
                type="button"
                onClick={() => onExplore()}
                className="touch-target flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-600 to-orange-600 px-6 py-4 text-base font-bold text-white shadow-lg shadow-orange-600/25 transition-all hover:from-emerald-500 hover:to-orange-500 active:scale-[0.98]"
              >
                <Search className="h-5 w-5 shrink-0" />
                Explorar vendedores
                <ChevronRight className="h-5 w-5 shrink-0" />
              </button>
            </>
          ) : (
            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => onRegisterClick()}
                className="touch-target flex flex-1 items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-600 to-emerald-700 px-6 py-4 text-base font-bold text-white shadow-lg shadow-emerald-600/25 active:scale-[0.98]"
              >
                <User className="h-5 w-5 shrink-0" />
                Criar conta grátis
              </button>
              <button
                type="button"
                onClick={() => onLoginClick()}
                className="touch-target flex flex-1 items-center justify-center gap-2 rounded-2xl border-2 border-slate-200 bg-white px-6 py-4 text-base font-bold text-slate-800 hover:bg-slate-50 active:scale-[0.98]"
              >
                <LogIn className="h-5 w-5 shrink-0 text-emerald-600" />
                Já tenho conta
              </button>
            </div>
          )}
          <button
            type="button"
            onClick={() => onBack()}
            className="mt-5 flex w-full touch-target items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white py-3.5 text-sm font-bold text-slate-700 shadow-sm hover:bg-slate-50 active:scale-[0.98]"
          >
            <ChevronLeft className="h-4 w-4 shrink-0" />
            Voltar
          </button>
        </div>
      </div>
    </div>
  );
}
